export const emitEvent = (event, data) => {
    document.dispatchEvent(new CustomEvent(event, {
        detail: data
    }))
}

export const onEvent = (event, callback) => {
    document.addEventListener(event, callback)
}

export const onLoaded = (callback) => {
    onEvent('DOMContentLoaded', callback)
}

// Initialize the initial state for the current page
export const initializeInitialState = () => {
    if (window.history && window.history.replaceState) {
        // Replace the initial state with our own state object
        // This ensures that when users navigate back to the initial page,
        // we have a proper state object to work with
        window.history.replaceState(
            {
                url: window.location.href,
                source: 'ajaxpress'
            },
            '',
            window.location.href
        );
    }
}

export const getContainer = () => {
    // Premium feature: custom target requires valid license
    const license = ajaxpress_vars?.license || {}
    const isLicenseValid = !!(license.key && license.status === 'active')
    
    let target = 'body'
    if (isLicenseValid && ajaxpress_vars.settings.target) {
        target = ajaxpress_vars.settings.target
    }
    
    return document.querySelector(target) || document.body
}

// Operations
window.ajaxpress_state = {}

// Global tracker for CORE scripts that should never re-execute (jQuery, etc.)
// These are libraries that would break if loaded twice
window.ajaxpress_core_scripts = window.ajaxpress_core_scripts || new Set()

// Global tracker for ALL executed external scripts (persists even if script tag is removed from DOM)
window.ajaxpress_executed_scripts = window.ajaxpress_executed_scripts || new Set()

// ============================================================================
// EVENT LISTENER TRACKING SYSTEM
// Track document/window listeners added after page load so we can clean them up
// before re-executing scripts on AJAX navigation
// ============================================================================

// Storage for tracked listeners
window.ajaxpress_tracked_listeners = window.ajaxpress_tracked_listeners || {
    document: [],
    window: []
}

// Store original addEventListener/removeEventListener methods
const originalDocAddListener = document.addEventListener.bind(document)
const originalDocRemoveListener = document.removeEventListener.bind(document)
const originalWinAddListener = window.addEventListener.bind(window)
const originalWinRemoveListener = window.removeEventListener.bind(window)

// Flag to control when we start tracking (after initial page load)
let isTrackingListeners = false

// Marker to identify AjaxPress's own listeners (these should NOT be removed)
const AJAXPRESS_LISTENER_MARKER = '__ajaxpress__'

/**
 * Check if a listener is AjaxPress's own listener
 */
const isAjaxPressListener = (listener) => {
    return listener && listener[AJAXPRESS_LISTENER_MARKER] === true
}

/**
 * Mark a listener as belonging to AjaxPress
 */
export const markAsAjaxPressListener = (listener) => {
    if (listener) {
        listener[AJAXPRESS_LISTENER_MARKER] = true
    }
    return listener
}

/**
 * Start tracking event listeners (call after initial page load)
 */
export const startTrackingListeners = () => {
    if (isTrackingListeners) return

    isTrackingListeners = true

    // Wrap document.addEventListener
    document.addEventListener = (type, listener, options) => {
        // Track non-AjaxPress listeners
        if (isTrackingListeners && listener && !isAjaxPressListener(listener)) {
            window.ajaxpress_tracked_listeners.document.push({ type, listener, options })
        }
        return originalDocAddListener(type, listener, options)
    }

    // Wrap window.addEventListener
    window.addEventListener = (type, listener, options) => {
        // Track non-AjaxPress listeners
        if (isTrackingListeners && listener && !isAjaxPressListener(listener)) {
            window.ajaxpress_tracked_listeners.window.push({ type, listener, options })
        }
        return originalWinAddListener(type, listener, options)
    }
}

/**
 * Cleanup all tracked event listeners
 * Called before re-executing scripts on AJAX navigation
 */
export const cleanupTrackedListeners = () => {
    // Remove all tracked document listeners
    window.ajaxpress_tracked_listeners.document.forEach(({ type, listener, options }) => {
        try {
            originalDocRemoveListener(type, listener, options)
        } catch (e) {
            // Ignore errors (listener may already be removed)
        }
    })

    // Remove all tracked window listeners
    window.ajaxpress_tracked_listeners.window.forEach(({ type, listener, options }) => {
        try {
            originalWinRemoveListener(type, listener, options)
        } catch (e) {
            // Ignore errors
        }
    })

    // Clear tracking arrays
    window.ajaxpress_tracked_listeners.document = []
    window.ajaxpress_tracked_listeners.window = []
}

// ============================================================================
// END EVENT LISTENER TRACKING SYSTEM
// ============================================================================

// ============================================================================
// TIMER & OBSERVER TRACKING SYSTEM
// Track setTimeout, setInterval, and MutationObserver created after page load
// so we can clean them up before re-executing scripts on AJAX navigation
// ============================================================================

// Storage for tracked timers and observers
window.ajaxpress_tracked_timers = window.ajaxpress_tracked_timers || {
    timeouts: new Set(),
    intervals: new Set(),
    observers: []
}

// Store original methods
const originalSetTimeout = window.setTimeout.bind(window)
const originalSetInterval = window.setInterval.bind(window)
const originalClearTimeout = window.clearTimeout.bind(window)
const originalClearInterval = window.clearInterval.bind(window)
const OriginalMutationObserver = window.MutationObserver

// Flag to control when we start tracking (after initial page load)
let isTrackingTimers = false

/**
 * Start tracking timers and observers (call after initial page load)
 */
export const startTrackingTimers = () => {
    if (isTrackingTimers) return
    isTrackingTimers = true

    // Wrap setTimeout
    window.setTimeout = (callback, delay, ...args) => {
        const wrappedCallback = (...innerArgs) => {
            // Remove from tracking when executed (one-shot)
            window.ajaxpress_tracked_timers.timeouts.delete(id)
            if (typeof callback === 'function') {
                callback(...innerArgs)
            } else if (typeof callback === 'string') {
                // Handle string callbacks (rare, but possible)
                eval(callback)
            }
        }
        const id = originalSetTimeout(wrappedCallback, delay, ...args)
        if (isTrackingTimers) {
            window.ajaxpress_tracked_timers.timeouts.add(id)
        }
        return id
    }

    // Wrap setInterval
    window.setInterval = (callback, delay, ...args) => {
        const id = originalSetInterval(callback, delay, ...args)
        if (isTrackingTimers) {
            window.ajaxpress_tracked_timers.intervals.add(id)
        }
        return id
    }

    // Wrap clearTimeout
    window.clearTimeout = (id) => {
        window.ajaxpress_tracked_timers.timeouts.delete(id)
        return originalClearTimeout(id)
    }

    // Wrap clearInterval
    window.clearInterval = (id) => {
        window.ajaxpress_tracked_timers.intervals.delete(id)
        return originalClearInterval(id)
    }

    // Wrap MutationObserver
    window.MutationObserver = class TrackedMutationObserver extends OriginalMutationObserver {
        constructor(callback) {
            super(callback)
            if (isTrackingTimers) {
                window.ajaxpress_tracked_timers.observers.push(this)
            }
        }

        disconnect() {
            const index = window.ajaxpress_tracked_timers.observers.indexOf(this)
            if (index > -1) {
                window.ajaxpress_tracked_timers.observers.splice(index, 1)
            }
            super.disconnect()
        }
    }
}

/**
 * Cleanup only TRACKED timers and observers
 * Called before re-executing scripts on AJAX navigation
 * Only clears timers created after tracking started - preserves sliders, countdowns, etc.
 */
export const cleanupTrackedTimers = () => {
    // Clear only tracked timeouts (not ALL timeouts)
    window.ajaxpress_tracked_timers.timeouts.forEach(id => {
        try {
            originalClearTimeout(id)
        } catch (e) {
            // Ignore errors
        }
    })

    // Clear only tracked intervals (not ALL intervals)
    window.ajaxpress_tracked_timers.intervals.forEach(id => {
        try {
            originalClearInterval(id)
        } catch (e) {
            // Ignore errors
        }
    })

    // Clear tracking sets
    window.ajaxpress_tracked_timers.timeouts.clear()
    window.ajaxpress_tracked_timers.intervals.clear()

    // Disconnect all tracked MutationObservers
    window.ajaxpress_tracked_timers.observers.forEach(observer => {
        try {
            observer.disconnect()
        } catch (e) {
            // Ignore errors
        }
    })
    window.ajaxpress_tracked_timers.observers = []
}

// ============================================================================
// END TIMER & OBSERVER TRACKING SYSTEM
// ============================================================================

/**
 * Core scripts that should NEVER be re-executed (would cause conflicts/errors)
 * These are typically libraries, not initialization scripts
 */
const CORE_SCRIPT_PATTERNS = [
    // jQuery ecosystem - must never reload
    'jquery.min.js',
    'jquery.js',
    'jquery-migrate',
    'jquery-core',
    'jquery-ui',
    'jquery.ui',
    // WordPress core libraries
    'wp-polyfill',
    'wp-hooks',
    'wp-i18n',
    'wp-api-fetch',
    'wp-dom-ready',
    'wp-url',
    'wp-embed.min.js',
    'wp-includes/js/dist/', // All WP block editor scripts
    // React/framework libraries
    'react.min.js',
    'react.production',
    'react.development',
    'react-dom.min.js',
    'react-dom.production',
    'react-dom.development',
    // Utility libraries
    'lodash.min.js',
    'lodash.js',
    'underscore.min.js',
    'underscore.js',
    'backbone.min.js',
    'backbone.js',
    'moment.min.js',
    'moment-timezone',
    // Media libraries
    'mediaelement',
    'wp-mediaelement',
    'plyr.min.js',
    'video.min.js',
    // Build/runtime tools
    'regenerator-runtime',
    'core-js',
    // Bundled theme/plugin scripts (Vite/Webpack builds)
    // These contain module-level const/let that can't be re-declared
    '/assets/js/script.js',
    '/assets/js/main.js',
    '/assets/js/app.js',
    '/assets/js/bundle.js',
    '/dist/js/',
    '/build/js/',
]

/**
 * Check if script is a core library that should not re-execute
 */
const isCoreScript = (src) => {
    if (!src) return false
    const normalizedSrc = normalizeUrl(src).toLowerCase()
    return CORE_SCRIPT_PATTERNS.some(pattern => normalizedSrc.includes(pattern.toLowerCase()))
}

/**
 * Check if script is AjaxPress's own frontend script
 */
const isAjaxPressScript = (src) => {
    if (!src) return false
    const normalizedSrc = normalizeUrl(src)
    return normalizedSrc.includes('ajaxpress.min.js') || (normalizedSrc.includes('/ajaxpress/') && normalizedSrc.includes('.min.js'))
}

/**
 * Check if script is excluded from re-execution by user settings
 * Supports partial URL matches and regex patterns (wrapped in //)
 */
const isExcludedScript = (src) => {
    if (!src) return false

    const excludeList = ajaxpress_vars?.settings?.reinit_scripts_exclude
    if (!excludeList || typeof excludeList !== 'string') return false

    const patterns = excludeList.split('\n').map(p => p.trim()).filter(p => p.length > 0)
    if (patterns.length === 0) return false

    const normalizedSrc = normalizeUrl(src).toLowerCase()

    return patterns.some(pattern => {
        // Check if pattern is a regex (wrapped in //)
        if (pattern.startsWith('/') && pattern.endsWith('/') && pattern.length > 2) {
            try {
                const regex = new RegExp(pattern.slice(1, -1), 'i')
                return regex.test(normalizedSrc)
            } catch (e) {
                // Invalid regex, treat as plain text
                return normalizedSrc.includes(pattern.toLowerCase())
            }
        }
        // Plain text partial match
        return normalizedSrc.includes(pattern.toLowerCase())
    })
}

/**
 * Get script path without query string for comparison
 */
const getScriptPath = (url) => {
    try {
        const urlObj = new URL(url)
        return urlObj.origin + urlObj.pathname
    } catch (e) {
        return url.split('?')[0]
    }
}

/**
 * Check if a script has been executed (checks both full URL and path without query)
 */
const isScriptExecuted = (normalizedSrc) => {
    const scriptPath = getScriptPath(normalizedSrc)

    // Check our tracking set
    if (window.ajaxpress_executed_scripts.has(normalizedSrc)) {
        return true
    }

    // Also check path without query string (for versioned scripts)
    for (const executed of window.ajaxpress_executed_scripts) {
        if (getScriptPath(executed) === scriptPath) {
            return true
        }
    }

    // Also check if script tag exists in current DOM (double-check)
    const existingScripts = document.querySelectorAll('script[src]')
    for (const script of existingScripts) {
        const existingSrc = normalizeUrl(script.src)
        const existingPath = getScriptPath(existingSrc)
        if (existingSrc === normalizedSrc || existingPath === scriptPath) {
            // Add to tracking set for future checks
            window.ajaxpress_executed_scripts.add(existingSrc)
            return true
        }
    }

    return false
}

/**
 * Initialize the script tracker with scripts already on the page
 * Tracks all external scripts to prevent re-execution
 */
/**
 * Track all current scripts in the DOM
 */
const trackCurrentScripts = (source = 'init') => {
    const existingScripts = document.querySelectorAll('script[src]')
    let newlyTracked = 0
    existingScripts.forEach(script => {
        if (script.src && !isAjaxPressScript(script.src)) {
            const normalizedSrc = normalizeUrl(script.src)
            // Skip if already tracked
            if (window.ajaxpress_executed_scripts.has(normalizedSrc)) {
                return
            }
            // Check path match too
            const scriptPath = getScriptPath(normalizedSrc)
            let alreadyTracked = false
            for (const executed of window.ajaxpress_executed_scripts) {
                if (getScriptPath(executed) === scriptPath) {
                    alreadyTracked = true
                    break
                }
            }
            if (alreadyTracked) return

            // Track core scripts
            if (isCoreScript(normalizedSrc)) {
                window.ajaxpress_core_scripts.add(normalizedSrc)
            }
            // Track ALL executed scripts to prevent re-execution
            window.ajaxpress_executed_scripts.add(normalizedSrc)
            newlyTracked++
        }
    })
    return newlyTracked
}

export const initializeScriptTracker = () => {
    // Track scripts currently in DOM
    trackCurrentScripts('init')

    // Also track scripts after window.load (catches deferred/footer scripts)
    if (document.readyState === 'complete') {
        // Already loaded, track again
        trackCurrentScripts('late-init')
    } else {
        window.addEventListener('load', () => {
            trackCurrentScripts('window.load')
        })
    }
}

export const fetchContent = async (url = window.location.href, source = 'click') => {
    return new Promise ( (resolve, reject) => {
        // Don't emit loading event for prefetch - it should be silent
        if (source !== 'prefetch') {
            emitEvent('ajaxpress-loading', {
                url, source
            })
        }
        const headers = new Headers();
        headers.set('AjaxPress-Ajax', 'true');
        // Fetch content from server.
        fetch(url, {
            headers: headers
        })
        .then( response => response.text() )
        .then( html => {
            window.ajaxpress_state.html = html
            // Don't emit fetched event for prefetch - it should be silent
            if (source !== 'prefetch') {
                emitEvent('ajaxpress-fetched', {
                    url, html: html || '', source
                })
            }
            resolve ( html )
        }).catch( err => {
            reject(err)
        })
    })
}


/**
 * Normalize URL to absolute URL
 */
const normalizeUrl = (url) => {
    if (!url) return url

    let fullUrl = url

    // Handle protocol-relative URLs
    if (url.startsWith('//')) {
        fullUrl = window.location.protocol + url
    }
    // Handle absolute URLs (already have protocol)
    else if (url.startsWith('http://') || url.startsWith('https://')) {
        fullUrl = url
    }
    // Handle root-relative URLs
    else if (url.startsWith('/')) {
        fullUrl = window.location.origin + url
    }
    // Handle relative URLs
    else {
        const baseUrl = window.location.href.split('?')[0]
        const basePath = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1)
        fullUrl = basePath + url
    }

    // Normalize by removing trailing slashes and lowercasing the domain
    try {
        const urlObj = new URL(fullUrl)
        return urlObj.href
    } catch (e) {
        return fullUrl
    }
}

/**
 * Check if script/stylesheet already exists (normalized URL comparison)
 */
const assetExists = (url, type = 'script') => {
    const normalizedUrl = normalizeUrl(url)
    const urlPath = getScriptPath(normalizedUrl)
    const selector = type === 'script' ? 'script[src]' : 'link[rel="stylesheet"][href]'
    const elements = document.querySelectorAll(selector)

    for (const el of elements) {
        const existingUrl = type === 'script' ? el.src : el.href
        const normalizedExisting = normalizeUrl(existingUrl)
        // Check both full URL match and path match (ignoring query strings)
        if (normalizedUrl === normalizedExisting ||
            existingUrl === url ||
            getScriptPath(normalizedExisting) === urlPath) {
            return true
        }
    }
    return false
}

/**
 * Load a script dynamically
 * @param {string} src - Script source URL
 * @param {object} options - Script options (async, defer, type)
 */
const loadScript = (src, options = {}) => {
    return new Promise((resolve) => {
        const normalizedSrc = normalizeUrl(src)

        // Check if script already exists
        if (assetExists(normalizedSrc, 'script')) {
            resolve()
            return
        }
        const script = document.createElement('script')
        script.src = normalizedSrc

        // Handle async/defer properly
        // If defer is set, don't set async (defer takes precedence for ordered execution)
        if (options.defer) {
            script.defer = true
            script.async = false
        } else if (options.async) {
            script.async = true
        } else {
            // Default: no async, no defer - blocks and executes in order
            script.async = false
        }

        if (options.type && options.type !== 'text/javascript') {
            script.type = options.type
        }

        script.onload = () => {
            resolve()
        }
        script.onerror = () => {
            // Silent fail
            // Resolve instead of reject to not break the chain
            resolve()
        }
        document.head.appendChild(script)
    })
}

/**
 * Load scripts sequentially to preserve execution order
 * @param {Array} scripts - Array of script objects {src, async, defer, type}
 */
const loadScriptsSequentially = async (scripts) => {
    for (const scriptInfo of scripts) {
        await loadScript(scriptInfo.src, {
            async: scriptInfo.async,
            defer: scriptInfo.defer,
            type: scriptInfo.type
        })
    }
}

/**
 * Load a stylesheet dynamically
 */
const loadStylesheet = (href) => {
    return new Promise((resolve, reject) => {
        const normalizedHref = normalizeUrl(href)
        
        // Check if stylesheet already exists
        if (assetExists(normalizedHref, 'stylesheet')) {
            resolve()
            return
        }

        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = normalizedHref
        link.onload = () => {
            resolve()
        }
        link.onerror = () => {
            // Silent fail
            reject(new Error(`Failed to load stylesheet: ${normalizedHref}`))
        }
        document.head.appendChild(link)
    })
}

/**
 * Check if inline script should be SKIPPED during re-execution
 * Returns true for scripts that should NEVER re-execute (blacklist approach)
 * All other scripts WILL be re-executed with IIFE wrapping
 */
const shouldSkipInlineScript = (content, scriptType) => {
    // JSON-LD is structured data, not executable JS. Always skip.
    if (scriptType === 'application/ld+json') return true

    if (!content) return true
    const trimmed = content.trim()

    // Skip empty scripts
    if (trimmed.length === 0) return true

    // Skip HTML templates (Elementor uses <script type="text/template">)
    if (trimmed.startsWith('<') || trimmed.includes('</div>') || trimmed.includes('</span>')) {
        return true
    }

    // Skip pure JSON data (starts with { or [ and looks like JSON)
    // JSON objects/arrays can't be executed as statements
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        // Check if it's actually JSON (no variable assignment at start)
        // Valid JS would be: var x = {...} or window.x = {...}
        // Invalid as statement: {...} or [...]
        try {
            JSON.parse(trimmed)
            return true // It's valid JSON, skip it
        } catch (e) {
            // Not valid JSON, might be a code block - but if it starts with { and has : right after
            // it's likely a labeled statement issue or object literal without assignment
            if (trimmed.startsWith('{') && /^\{\s*["']?\w+["']?\s*:/.test(trimmed)) {
                return true // Looks like an object literal without assignment
            }
        }
    }

    // Skip wp.i18n locale data (already set, would just duplicate)
    if (trimmed.includes('wp.i18n.setLocaleData')) {
        return true
    }

    // Skip short IIFE that check/set body classes (customize-support detection)
    if (trimmed.startsWith('(function') && trimmed.length < 500 &&
        (trimmed.includes('className') || trimmed.includes('classList')) &&
        !trimmed.includes('slider') && !trimmed.includes('Slider')) {
        return true
    }

    // Execute everything else
    return false
}

/**
 * Check if inline script is a "data script" (should run before external scripts)
 * Data scripts define variables/config but don't call jQuery methods
 * Returns true for scripts that are safe to run before libraries load
 */
const isDataScript = (content) => {
    if (!content) return false
    const trimmed = content.trim()

    // Check if it's primarily a variable assignment (localized data)
    // Patterns: var x = {...}, window.x = {...}, const x = {...}, let x = {...}
    if (/^(var|const|let)\s+\w+\s*=/.test(trimmed)) {
        // But if it contains jQuery calls, it's an init script, not data
        if (/\$\s*\(/.test(trimmed) || /jQuery\s*\(/.test(trimmed)) {
            // Check if it's just defining a function that uses jQuery (that's ok)
            // vs actually calling jQuery methods immediately
            if (/\$\([^)]*\)\.\w+\(/.test(trimmed) || /jQuery\([^)]*\)\.\w+\(/.test(trimmed)) {
                return false // Has immediate jQuery method calls - it's init script
            }
        }
        return true // Data script
    }

    // window.x = {...} pattern
    if (/^window\.\w+\s*=/.test(trimmed)) {
        if (/\$\([^)]*\)\.\w+\(/.test(trimmed) || /jQuery\([^)]*\)\.\w+\(/.test(trimmed)) {
            return false
        }
        return true
    }

    // CDATA wrapped scripts are usually localized data
    if (trimmed.includes('/* <![CDATA[ */')) {
        return true
    }

    // Gravity Forms initialization - MUST run before GF external scripts
    // These define gform object, config, and methods (not calling them)
    if (trimmed.includes('gform') && (
        /var\s+gform/.test(trimmed) || // var gform = ... or var gform;
        /gform_theme_config\s*=/.test(trimmed) || // gform_theme_config = {...}
        /gform_gravityforms_theme_config\s*=/.test(trimmed) ||
        /gform\.\w+\s*=\s*function/.test(trimmed) || // gform.method = function() {...}
        /gform\[['"]/.test(trimmed) // gform['method'] = ...
    )) {
        return true
    }

    return false
}

/**
 * Check if inline script is an "init script" (should run after external scripts)
 * Init scripts call jQuery methods and depend on libraries being loaded
 */
const isInitScript = (content) => {
    if (!content) return false
    const trimmed = content.trim()

    // Scripts that start with jQuery/$ calls are init scripts
    if (/^\$\s*\(/.test(trimmed) || /^jQuery\s*\(/.test(trimmed)) {
        return true
    }

    // Scripts that contain immediate jQuery method calls
    if (/\$\([^)]*\)\.\w+\(/.test(trimmed) || /jQuery\([^)]*\)\.\w+\(/.test(trimmed)) {
        return true
    }

    // Scripts that use document.ready with jQuery
    if (/\$\s*\(\s*(document|function)/.test(trimmed) || /jQuery\s*\(\s*(document|function)/.test(trimmed)) {
        return true
    }

    return false
}

/**
 * Execute an inline script wrapped in IIFE to prevent const/let redeclaration errors
 * All inline scripts are wrapped in IIFE for scope isolation
 * var/const/let declarations are converted to window assignments to keep them global
 */
const executeInlineScript = (scriptContent) => {
    if (!scriptContent) return

    let content = scriptContent.trim()
    if (content.length === 0) return

    // Remove CDATA wrappers
    content = content.replace(/\/\*\s*<!\[CDATA\[\s*\*\//g, '')
    content = content.replace(/\/\*\s*\]\]>\s*\*\//g, '')
    content = content.trim()

    // Check if script uses const/let (requires IIFE to prevent redeclaration errors)
    const usesBlockScoped = /\b(const|let)\s+/.test(content)

    if (usesBlockScoped) {
        // Script uses const/let - must wrap in IIFE to prevent redeclaration errors
        // Convert declarations to window assignments
        content = content.replace(/^(var|const|let)\s+(\w+)(\s*[=;])/gm, 'window.$2$3')

        const wrapped = `(function($, jQuery, window, document, undefined) {
${content}
})(window.jQuery, window.jQuery, window, document);`

        try {
            const fn = new Function(wrapped)
            fn()
        } catch (e) {
            console.warn('[AjaxPress] Inline script error:', e.message)
        }
    } else {
        // Script only uses var (or no declarations) - execute in global scope
        // This is important for scripts like Gravity Forms that use patterns like:
        // var gform; gform || (gform = {...})
        try {
            const fn = new Function(content)
            fn()
        } catch (e) {
            console.warn('[AjaxPress] Inline script error:', e.message)
        }
    }
}

/**
 * Execute scripts from a container
 * @param {HTMLElement} container - The container element
 * @param {Object} options - Options object
 * @param {boolean} options.forceReload - If true, re-execute all scripts (for reinit_scripts mode)
 */
const executeScripts = (container, options = {}) => {
    const { forceReload = false } = options

    // When forceReload is false (reinit_scripts OFF), skip ALL script execution
    if (!forceReload) {
        return
    }

    // Use static array to avoid NodeList mutation issues during iteration
    const scripts = Array.from(container.querySelectorAll('script'))
    const executedInThisCycle = new Set()

    // THREE-PASS EXECUTION:
    // Pass 1: Execute DATA inline scripts (localized data like wpData - no jQuery calls)
    // Pass 2: Execute external scripts (libraries like flexslider.js)
    // Pass 3: Execute INIT inline scripts (jQuery-based initialization)

    // PASS 1: Data inline scripts (run BEFORE external scripts)
    scripts.forEach((oldScript, index) => {
        if (oldScript.src) return // Skip external scripts

        const scriptContent = oldScript.textContent
        const scriptId = scriptContent ? scriptContent.substring(0, 100) : 'empty-' + index

        if (executedInThisCycle.has(scriptId)) return
        if (shouldSkipInlineScript(scriptContent)) return
        if (!forceReload) return

        // Only execute data scripts in pass 1 (no jQuery calls)
        if (isDataScript(scriptContent)) {
            executedInThisCycle.add(scriptId)
            executeInlineScript(scriptContent)
        }
    })

    // PASS 2: External scripts - collect promises for load tracking
    const scriptLoadPromises = []

    scripts.forEach((oldScript, index) => {
        if (!oldScript.src) return // Skip inline scripts

        // Skip AjaxPress's own script
        if (isAjaxPressScript(oldScript.src)) {
            return
        }

        const scriptId = oldScript.src

        if (executedInThisCycle.has(scriptId)) {
            return
        }

        executedInThisCycle.add(scriptId)

        const rawSrc = oldScript.getAttribute('src')
        const resolvedSrc = oldScript.src
        const normalizedSrc = normalizeUrl(resolvedSrc || rawSrc)

        // Skip core scripts (jQuery, React, etc.) only if they were already loaded
        // This allows core scripts to load when navigating to a page that needs them
        if (isCoreScript(normalizedSrc) && isScriptExecuted(normalizedSrc)) {
            return
        }

        // Allow force skip with data-ajaxpress-skip attribute
        if (oldScript.hasAttribute('data-ajaxpress-skip')) {
            return
        }

        // Skip scripts excluded by user settings
        if (isExcludedScript(normalizedSrc)) {
            return
        }

        // When forceReload is true, only skip core scripts (already handled above)
        // When forceReload is false, also skip already executed scripts
        if (!forceReload && isScriptExecuted(normalizedSrc)) {
            return
        }

        // Track this script as executed (for non-forceReload mode)
        if (!forceReload) {
            window.ajaxpress_executed_scripts.add(normalizedSrc)
        }

        const newScript = document.createElement('script')
        Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value)
        })
        newScript.src = normalizedSrc

        // Mark as executed by AjaxPress
        newScript.setAttribute('data-ajaxpress-executed', 'true')

        // Create promise to track script load
        const loadPromise = new Promise(resolve => {
            newScript.onload = () => resolve()
            newScript.onerror = () => resolve() // Resolve even on error to not block
            // Fallback timeout in case onload doesn't fire
            setTimeout(resolve, 2000)
        })
        scriptLoadPromises.push(loadPromise)

        // Remove old script and add new one to trigger execution
        oldScript.remove()
        container.appendChild(newScript)
    })

    // PASS 3: Init inline scripts (run AFTER external scripts have loaded)
    if (forceReload) {
        // Wait for all external scripts to load, then execute inline scripts
        Promise.all(scriptLoadPromises).then(() => {
            // Additional small delay for script execution to complete
            setTimeout(() => {
                scripts.forEach((oldScript, index) => {
                    if (oldScript.src) return // Skip external scripts

                    const scriptContent = oldScript.textContent
                    const scriptId = scriptContent ? scriptContent.substring(0, 100) : 'empty-' + index

                    if (executedInThisCycle.has(scriptId)) return
                    if (shouldSkipInlineScript(scriptContent)) return

                    // Execute remaining inline scripts (init scripts with jQuery calls)
                    // Data scripts were already executed in pass 1
                    if (!isDataScript(scriptContent)) {
                        executedInThisCycle.add(scriptId)
                        executeInlineScript(scriptContent)
                    }
                })
            }, 50)
        }).catch(() => {
            // Script loading errors are handled individually, this is a fallback
        })
    }
}

/**
 * Sync body classes from new document to current document
 * Preserves certain classes that shouldn't change (admin-bar, etc.)
 */
const syncBodyClasses = (newDocument) => {
    const currentBody = document.body
    const newBody = newDocument.body

    if (!newBody) return

    // Classes to always preserve (shouldn't be removed during navigation)
    const preserveClasses = [
        'admin-bar',
        'logged-in',
        'customize-support',
        'no-customize-support',
        'wp-embed-responsive',
        // AjaxPress animation classes
        'ajaxpress-animate-fade-out',
        'ajaxpress-animate-fade-in',
        'ajaxpress-animate-slide-out',
        'ajaxpress-animate-slide-in',
        'ajaxpress-animate-flip-out',
        'ajaxpress-animate-flip-in',
        'ajaxpress-animate-scale-out',
        'ajaxpress-animate-scale-in',
        'ajaxpress-animate-cursor',
        'ajaxpress-enhanced-focus',
        'ajaxpress-keyboard-user'
    ]

    // Get current preserved classes
    const preservedClasses = []
    preserveClasses.forEach(cls => {
        if (currentBody.classList.contains(cls)) {
            preservedClasses.push(cls)
        }
    })

    // Replace body class entirely with new page's classes
    currentBody.className = newBody.className

    // Re-add preserved classes if they were present
    preservedClasses.forEach(cls => {
        if (!currentBody.classList.contains(cls)) {
            currentBody.classList.add(cls)
        }
    })
}

/**
 * Replace JSON-LD structured data wholesale.
 * JSON-LD is data, not code: every navigation should swap the previous page's
 * blocks for the new ones. Hoists any body-level JSON-LD into <head> so the
 * container swap can't leave a duplicate behind.
 */
const replaceJsonLd = (newDocument) => {
    const selector = 'script[type="application/ld+json"]'

    document.querySelectorAll(selector).forEach(node => node.remove())

    const seen = new Set()
    Array.from(newDocument.querySelectorAll(selector)).forEach(node => {
        const key = (node.textContent || '').trim()
        if (!key || seen.has(key)) return
        seen.add(key)
        document.head.appendChild(node.cloneNode(true))
    })
}

/**
 * Update head elements (scripts, stylesheets, meta tags, inline styles)
 */
const updateHead = async (newDocument, options = {}) => {
    const { reinitScripts = false } = options
    const currentHead = document.head
    const newHead = newDocument.head

    replaceJsonLd(newDocument)

    // Collect new stylesheets and inline styles from new document
    const newStylesheets = new Set()
    const newInlineStyleIds = new Set()

    Array.from(newHead.querySelectorAll('link[rel="stylesheet"]')).forEach(link => {
        const href = link.getAttribute('href')
        if (href) {
            newStylesheets.add(normalizeUrl(href))
        }
    })

    Array.from(newHead.querySelectorAll('style[id]')).forEach(style => {
        if (style.id) newInlineStyleIds.add(style.id)
    })

    // Remove old page-specific stylesheets that don't exist in new page
    // Keep core WP styles, theme base styles, and plugin base styles
    const protectedPatterns = [
        // WordPress core
        'wp-includes',
        'admin-bar',
        'dashicons',
        // AjaxPress
        'ajaxpress.min.css',
        'ajaxpress-inline-css',
        // All theme stylesheets (covers all themes)
        '/themes/',
        // Popular page builders
        'elementor',
        'eael-',
        'divi',
        'wpbakery',
        'js_composer',
        // Popular plugins
        'woocommerce',
        'jetpack',
        'contact-form-7',
        // Common CSS libraries
        'font-awesome',
        'fontawesome',
        'dashicons',
        'google-fonts',
        'webfonts'
    ]

    Array.from(currentHead.querySelectorAll('link[rel="stylesheet"]')).forEach(link => {
        const href = link.getAttribute('href')
        if (!href) return

        const normalizedHref = normalizeUrl(href)

        // Check if protected
        const isProtected = protectedPatterns.some(pattern => normalizedHref.includes(pattern))
        if (isProtected) return

        // Remove if not in new page's stylesheets
        if (!newStylesheets.has(normalizedHref)) {
            link.remove()
        }
    })

    // Remove old inline styles that don't exist in new page
    Array.from(currentHead.querySelectorAll('style[id]')).forEach(style => {
        // Skip empty IDs
        if (!style.id) return

        // Keep ajaxpress styles
        if (style.id.includes('ajaxpress')) return

        // Check if this style ID exists in new document
        const existsInNew = newHead.querySelector(`style#${CSS.escape(style.id)}`)
        if (!existsInNew) {
            // Page-specific style not in new page, remove it
            style.remove()
        }
    })

    // Process new head elements
    // Collect scripts in order (both external and inline) to preserve dependency order
    const scriptsInOrder = []
    const stylesheetsToLoad = []

    Array.from(newHead.children).forEach(newElement => {
        const tagName = newElement.tagName.toLowerCase()

        if (tagName === 'script') {
            // JSON-LD is handled wholesale by replaceJsonLd() — never let it
            // reach the executable-script path.
            if (newElement.getAttribute('type') === 'application/ld+json') {
                return
            }

            // When reinitScripts is OFF, skip ALL script processing
            if (!reinitScripts) {
                return
            }

            // Skip if already executed
            if (newElement.hasAttribute('data-ajaxpress-executed')) {
                return
            }

            const src = newElement.getAttribute('src')
            // Skip AjaxPress's own script
            if (src && isAjaxPressScript(src)) {
                return
            }

            if (src) {
                // External script
                const normalizedSrc = normalizeUrl(src)

                // Skip core scripts only if already loaded - allows first-time loading
                if ((isCoreScript(normalizedSrc) || window.ajaxpress_core_scripts.has(normalizedSrc)) && isScriptExecuted(normalizedSrc)) {
                    return
                }

                // Skip scripts excluded by user settings
                if (isExcludedScript(normalizedSrc)) {
                    return
                }

                // Track and add script (preserve order with inline scripts)
                window.ajaxpress_executed_scripts.add(normalizedSrc)
                scriptsInOrder.push({
                    type: 'external',
                    src: normalizedSrc,
                    async: newElement.getAttribute('async') !== null,
                    defer: newElement.getAttribute('defer') !== null,
                    scriptType: newElement.getAttribute('type') || 'text/javascript'
                })
            } else {
                // Inline script in head - add to queue to preserve order
                const scriptContent = newElement.textContent
                if (!shouldSkipInlineScript(scriptContent)) {
                    scriptsInOrder.push({
                        type: 'inline',
                        content: scriptContent
                    })
                }
            }
        } else if (tagName === 'link') {
            const rel = newElement.getAttribute('rel')
            const href = newElement.getAttribute('href')
            
            if (rel === 'stylesheet' && href) {
                const normalizedHref = normalizeUrl(href)
                if (!assetExists(normalizedHref, 'stylesheet')) {
                    stylesheetsToLoad.push(normalizedHref)
                }
            } else if (rel === 'preload' || rel === 'prefetch') {
                // Handle preload/prefetch links
                const normalizedHref = normalizeUrl(href)
                const existingPreload = currentHead.querySelector(`link[rel="${rel}"][href="${normalizedHref}"]`)
                if (!existingPreload && href) {
                    const link = document.createElement('link')
                    link.rel = rel
                    link.href = normalizedHref
                    if (newElement.getAttribute('as')) {
                        link.setAttribute('as', newElement.getAttribute('as'))
                    }
                    currentHead.appendChild(link)
                }
            }
        } else if (tagName === 'style') {
            // Inline styles - replace if exists, otherwise append
            const styleContent = newElement.textContent
            if (styleContent && styleContent.trim()) {
                const styleId = newElement.getAttribute('id')

                // Check if style with same ID already exists
                if (styleId) {
                    const existingStyle = currentHead.querySelector(`style#${CSS.escape(styleId)}`)
                    if (existingStyle) {
                        // Replace content of existing style
                        existingStyle.textContent = styleContent
                        if (newElement.getAttribute('media')) {
                            existingStyle.setAttribute('media', newElement.getAttribute('media'))
                        }
                        return // Skip creating new element
                    }
                }

                // Create new style element
                const style = document.createElement('style')
                style.textContent = styleContent
                if (styleId) {
                    style.id = styleId
                }
                if (newElement.getAttribute('media')) {
                    style.setAttribute('media', newElement.getAttribute('media'))
                }
                currentHead.appendChild(style)
            }
        } else if (tagName === 'meta') {
            // Update meta tags
            const name = newElement.getAttribute('name') || newElement.getAttribute('property')
            const content = newElement.getAttribute('content')
            
            if (name && content) {
                let existingMeta = currentHead.querySelector(`meta[name="${name}"], meta[property="${name}"]`)
                if (!existingMeta) {
                    existingMeta = document.createElement('meta')
                    if (newElement.getAttribute('name')) {
                        existingMeta.setAttribute('name', name)
                    } else {
                        existingMeta.setAttribute('property', name)
                    }
                    currentHead.appendChild(existingMeta)
                }
                existingMeta.setAttribute('content', content)
            }
        }
    })
    
    // Load stylesheets in parallel (order doesn't matter for CSS)
    const stylesheetPromises = stylesheetsToLoad.map(href => {
        return loadStylesheet(href).catch(err => {
            // Silent fail
        })
    })

    // Wait for stylesheets first (prevents FOUC)
    await Promise.all(stylesheetPromises)

    // Execute scripts in order (preserves dependency order between external and inline)
    for (const scriptInfo of scriptsInOrder) {
        if (scriptInfo.type === 'external') {
            await loadScript(scriptInfo.src, {
                async: scriptInfo.async,
                defer: scriptInfo.defer,
                type: scriptInfo.scriptType
            })
        } else if (scriptInfo.type === 'inline') {
            executeInlineScript(scriptInfo.content)
        }
    }
}

/**
 * Check if an element should be preserved (not touched during DOM update)
 * @param {Element} el - Element to check
 * @param {string[]} selectors - Array of CSS selectors for preserved elements
 * @returns {boolean}
 */
const shouldPreserveElement = (el, selectors) => {
    if (!el || !el.matches || selectors.length === 0) return false
    return selectors.some(sel => {
        try {
            // Check if element matches selector OR contains a matching element
            return el.matches(sel) || el.querySelector(sel)
        } catch {
            return false
        }
    })
}

/**
 * Selectively update DOM while preserving specific elements (iframes, audio, video)
 * This function updates the DOM around preserved elements without ever removing them
 * @param {Element} currentEl - Current DOM element
 * @param {Element} newEl - New element from fetched page
 * @param {string[]} preserveSelectors - CSS selectors for elements to preserve
 */
const selectiveUpdate = (currentEl, newEl, preserveSelectors) => {
    // If this element should be preserved, don't touch it at all
    if (currentEl.matches && preserveSelectors.some(sel => {
        try { return currentEl.matches(sel) } catch { return false }
    })) {
        return
    }

    // Sync attributes
    if (newEl.attributes) {
        Array.from(newEl.attributes).forEach(attr => {
            if (currentEl.getAttribute(attr.name) !== attr.value) {
                currentEl.setAttribute(attr.name, attr.value)
            }
        })
    }
    if (currentEl.attributes) {
        Array.from(currentEl.attributes).forEach(attr => {
            if (!newEl.hasAttribute(attr.name)) {
                currentEl.removeAttribute(attr.name)
            }
        })
    }

    // Process children
    const currentChildren = [...currentEl.childNodes]
    const newChildren = [...newEl.childNodes]

    let currentIndex = 0
    let newIndex = 0

    while (newIndex < newChildren.length || currentIndex < currentChildren.length) {
        let newChild = newChildren[newIndex]
        let currentChild = currentChildren[currentIndex]

        // Skip preserved elements in BOTH current and new DOM
        // This keeps them in sync and prevents element mismatch
        const currentIsPreserved = currentChild && currentChild.nodeType === 1 &&
            shouldPreserveElement(currentChild, preserveSelectors)
        const newIsPreserved = newChild && newChild.nodeType === 1 &&
            shouldPreserveElement(newChild, preserveSelectors)

        if (currentIsPreserved && newIsPreserved) {
            // Both are preserved - skip both, keep current untouched
            currentIndex++
            newIndex++
            continue
        }

        if (currentIsPreserved) {
            // Current is preserved but new is not - skip current, keep it
            currentIndex++
            continue
        }

        if (newIsPreserved) {
            // New is preserved but current is not - skip new (don't add duplicate)
            newIndex++
            continue
        }

        // Neither is preserved - proceed with update logic
        if (!newChild && !currentChild) {
            break
        }

        if (!currentChild) {
            // No more current children, append new ones
            currentEl.appendChild(newChild.cloneNode(true))
            newIndex++
            continue
        }

        if (!newChild) {
            // No more new children, remove current (unless preserved - already handled above)
            if (currentChild.parentNode) {
                currentChild.remove()
            }
            currentIndex++
            continue
        }

        if (currentChild.nodeType !== newChild.nodeType) {
            // Different node types, replace current with new
            const newNode = newChild.cloneNode(true)
            currentChild.replaceWith(newNode)
            currentIndex++
            newIndex++
        } else if (currentChild.nodeType === 3) {
            // Text nodes - update content if different
            if (currentChild.textContent !== newChild.textContent) {
                currentChild.textContent = newChild.textContent
            }
            currentIndex++
            newIndex++
        } else if (currentChild.nodeType === 1) {
            // Element nodes
            if (currentChild.tagName !== newChild.tagName) {
                // Different tags, replace
                const newNode = newChild.cloneNode(true)
                currentChild.replaceWith(newNode)
            } else {
                // Same tag, recurse into children
                selectiveUpdate(currentChild, newChild, preserveSelectors)
            }
            currentIndex++
            newIndex++
        } else {
            // Other node types (comments, etc.) - just move on
            currentIndex++
            newIndex++
        }
    }
}

export const updateContent = async (html, source = 'navigation') => {
    /**
     * Updates the main content container with new HTML and updates the document title.
     * Handles scripts, stylesheets, and reinitializes JavaScript.
     * @param {string} html - The HTML content to update
     * @param {string} source - The source of the update ('navigation', 'comment', etc.)
     */
    var currentContainer = getContainer();
    if (!currentContainer) return

    // Check if reinit_scripts is enabled (convert to boolean - handles "1", 1, true, etc.)
    const reinitScripts = !!(ajaxpress_vars?.settings?.reinit_scripts && ajaxpress_vars.settings.reinit_scripts !== '0' && ajaxpress_vars.settings.reinit_scripts !== 'false')

    // Store source for event handlers
    window.ajaxpress_state.updateSource = source

    // Update the URL.
    if (window.history && window.history.pushState && window.ajaxpress_state.url !== window.location.href) {
        window.history.pushState(
            {
                url: window.ajaxpress_state.url,
                source: 'ajaxpress'
            },
            '',
            window.ajaxpress_state.url
        );
    }

    // Parse the new HTML
    var parser = new window.DOMParser();
    var newDocument = parser.parseFromString(html, 'text/html');

    // Update title.
    document.title = newDocument.title;

    // Sync body classes from new document
    syncBodyClasses(newDocument);

    // Update head elements (scripts, stylesheets, meta tags)
    await updateHead(newDocument, { reinitScripts });

    // Get the new container content (Premium feature: custom target requires valid license)
    const license = ajaxpress_vars?.license || {}
    const isLicenseValid = !!(license.key && license.status === 'active')

    let target = 'body'
    if (isLicenseValid && ajaxpress_vars.settings.target) {
        target = ajaxpress_vars.settings.target
    }

    const newContainer = newDocument.querySelector(target)

    if (!newContainer) {
        currentContainer.innerHTML = html;
        executeScripts(currentContainer, { forceReload: reinitScripts });
        return
    }

    // =========================================================================
    // PRESERVE ELEMENTS SETUP
    // Parse selectors for elements that should never be touched (iframes, audio, video)
    // =========================================================================
    const preserveSelectors = (ajaxpress_vars.settings.ignore_elements || '')
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0)

    // Check if we need selective update (has preserved elements in current container)
    const hasPreservedElements = preserveSelectors.length > 0 &&
        preserveSelectors.some(sel => {
            try { return currentContainer.querySelector(sel) } catch { return false }
        })

    // =========================================================================
    // CONTENT UPDATE STRATEGY
    // If preserved elements exist: Use selective DOM update (never touch preserved elements)
    // Otherwise: Use fast innerHTML replacement
    // =========================================================================

    let finalContainer = currentContainer

    if (hasPreservedElements) {
        // SELECTIVE UPDATE - preserves iframes, audio, video in place
        // Never removes preserved elements from DOM, updates everything around them

        if (reinitScripts) {
            cleanupTrackedListeners()
            cleanupTrackedTimers()
        }

        // Use selective update instead of innerHTML
        selectiveUpdate(currentContainer, newContainer, preserveSelectors)
        finalContainer = currentContainer

        // JSON-LD has already been hoisted into <head>; strip any copies the
        // swap dragged into the container so search engines see one set.
        finalContainer.querySelectorAll('script[type="application/ld+json"]').forEach(s => s.remove())

        // Emit before-scripts event
        document.dispatchEvent(new CustomEvent('ajaxpress:before-scripts', {
            bubbles: true,
            detail: { container: finalContainer, url: window.ajaxpress_state.url }
        }))

        // Execute scripts
        executeScripts(finalContainer, { forceReload: reinitScripts })
        executeBodyScripts(newDocument, newContainer, { forceReload: reinitScripts })

    } else if (reinitScripts) {
        // CLEAN SLATE APPROACH (no preserved elements)
        cleanupTrackedListeners()
        cleanupTrackedTimers()

        const cleanContainer = currentContainer.cloneNode(false)
        cleanContainer.innerHTML = newContainer.innerHTML
        currentContainer.replaceWith(cleanContainer)
        finalContainer = cleanContainer

        finalContainer.querySelectorAll('script[type="application/ld+json"]').forEach(s => s.remove())

        document.dispatchEvent(new CustomEvent('ajaxpress:before-scripts', {
            bubbles: true,
            detail: { container: finalContainer, url: window.ajaxpress_state.url }
        }))

        executeScripts(finalContainer, { forceReload: true })
        executeBodyScripts(newDocument, newContainer, { forceReload: true })

    } else {
        // FAST PATH (no preserved elements, no reinit)
        currentContainer.innerHTML = newContainer.innerHTML
        finalContainer = currentContainer

        finalContainer.querySelectorAll('script[type="application/ld+json"]').forEach(s => s.remove())

        executeScripts(finalContainer, { forceReload: false })
        executeBodyScripts(newDocument, newContainer, { forceReload: false })
    }

    // Wait for scripts to execute before triggering reinitialization events
    setTimeout(() => {
        triggerPageLoadEvents(finalContainer)
    }, 150)
}

/**
 * Execute scripts from body that are outside the main container
 */
const executeBodyScripts = (newDocument, newContainer, options = {}) => {
    const { forceReload = false } = options

    // When forceReload is false (reinit_scripts OFF), skip ALL script execution
    if (!forceReload) {
        return
    }

    // Use static array to avoid NodeList mutation issues during iteration
    const bodyScripts = Array.from(newDocument.body.querySelectorAll('script'))
    const executedInThisCycle = new Set()

    if (bodyScripts.length === 0) return

    // THREE-PASS EXECUTION:
    // Pass 1: Execute DATA inline scripts (localized data - no jQuery calls)
    // Pass 2: Execute external scripts (libraries)
    // Pass 3: Execute INIT inline scripts (jQuery-based initialization)

    // PASS 1: Data inline scripts
    bodyScripts.forEach((oldScript, index) => {
        const scriptSrc = oldScript.getAttribute('src')
        if (scriptSrc) return // Skip external scripts

        // Skip scripts that are inside the container (already executed)
        if (newContainer.contains(oldScript)) return

        const scriptContent = oldScript.textContent
        const scriptId = scriptContent ? scriptContent.substring(0, 100) : 'body-empty-' + index

        if (executedInThisCycle.has(scriptId)) return
        if (shouldSkipInlineScript(scriptContent)) return
        if (!forceReload) return

        // Only execute data scripts in pass 1
        if (isDataScript(scriptContent)) {
            executedInThisCycle.add(scriptId)
            executeInlineScript(scriptContent)
        }
    })

    // PASS 2: External scripts - collect promises for load tracking
    const scriptLoadPromises = []

    bodyScripts.forEach(oldScript => {
        const scriptSrc = oldScript.getAttribute('src')
        if (!scriptSrc) return // Skip inline scripts

        // Skip scripts that are inside the container (already executed)
        if (newContainer.contains(oldScript)) return

        // Skip AjaxPress's own script
        if (isAjaxPressScript(scriptSrc)) return

        const normalizedSrc = normalizeUrl(scriptSrc)

        // Skip core scripts only if already loaded - allows first-time loading
        if (isCoreScript(normalizedSrc) && isScriptExecuted(normalizedSrc)) {
            return
        }

        // Skip if has data-ajaxpress-skip attribute
        if (oldScript.hasAttribute('data-ajaxpress-skip')) {
            return
        }

        // Skip scripts excluded by user settings
        if (isExcludedScript(normalizedSrc)) {
            return
        }

        // When forceReload is true, only skip core scripts (already handled above)
        // When forceReload is false, also skip already executed scripts
        if (!forceReload && isScriptExecuted(normalizedSrc)) {
            return
        }

        // Track this script as executed (for non-forceReload mode)
        if (!forceReload) {
            window.ajaxpress_executed_scripts.add(normalizedSrc)
        }

        const newScript = document.createElement('script')
        Array.from(oldScript.attributes).forEach(attr => {
            newScript.setAttribute(attr.name, attr.value)
        })
        newScript.src = normalizedSrc
        newScript.setAttribute('data-ajaxpress-executed', 'true')

        // Create promise to track script load
        const loadPromise = new Promise(resolve => {
            newScript.onload = () => resolve()
            newScript.onerror = () => resolve() // Resolve even on error to not block
            // Fallback timeout in case onload doesn't fire
            setTimeout(resolve, 2000)
        })
        scriptLoadPromises.push(loadPromise)

        document.body.appendChild(newScript)
    })

    // PASS 3: Init inline scripts (run AFTER external scripts have loaded)
    if (forceReload) {
        // Wait for all external scripts to load, then execute inline scripts
        Promise.all(scriptLoadPromises).then(() => {
            // Additional small delay for script execution to complete
            setTimeout(() => {
                bodyScripts.forEach((oldScript, index) => {
                    const scriptSrc = oldScript.getAttribute('src')
                    if (scriptSrc) return // Skip external scripts

                    // Skip scripts that are inside the container (already executed)
                    if (newContainer.contains(oldScript)) return

                    const scriptContent = oldScript.textContent
                    const scriptId = scriptContent ? scriptContent.substring(0, 100) : 'body-empty-' + index

                    if (executedInThisCycle.has(scriptId)) return
                    if (shouldSkipInlineScript(scriptContent)) return

                    // Execute remaining inline scripts (init scripts)
                    if (!isDataScript(scriptContent)) {
                        executedInThisCycle.add(scriptId)
                        executeInlineScript(scriptContent)
                    }
                })
            }, 50)
        }).catch(() => {
            // Script loading errors are handled individually, this is a fallback
        })
    }
}

/**
 * Reinitialize common lazy load libraries
 * Detects which libraries are present and calls their refresh/update methods
 */
const reinitLazyLoaders = () => {

    // lazySizes (most popular WP lazy load library)
    if (typeof window.lazySizes !== 'undefined') {
        window.lazySizes.init()
    }

    // lozad.js
    if (typeof window.lozad !== 'undefined') {
        const observer = window.lozad()
        observer.observe()
    }

    // vanilla-lazyload
    if (typeof window.LazyLoad !== 'undefined' && window.lazyLoadInstance) {
        window.lazyLoadInstance.update()
    }

    // Native lazy loading - force reload by resetting src
    // Only for images that haven't loaded yet
    document.querySelectorAll('img[loading="lazy"]:not([data-ajaxpress-lazy-loaded])').forEach(img => {
        if (!img.complete) {
            const src = img.src
            img.src = ''
            img.src = src
            img.setAttribute('data-ajaxpress-lazy-loaded', 'true')
        }
    })

    // WordPress native lazy loading (wp-lazy-loading)
    if (typeof window.wp !== 'undefined' && window.wp.lazyLoad) {
        window.wp.lazyLoad.update()
    }
}

/**
 * Reinitialize theme and plugin scripts after AJAX navigation
 *
 * NOTE: With universal script re-execution enabled (reinit_scripts=true),
 * most plugins will reinitialize automatically when their scripts re-execute.
 * This function handles popular plugins that need explicit reinitialization.
 */
const reinitThemeScripts = () => {
    // Gravity Forms reinitialization
    if (typeof window.gform !== 'undefined' && typeof jQuery !== 'undefined') {
        try {
            jQuery('.gform_wrapper form').each(function() {
                const formId = jQuery(this).attr('id')?.replace('gform_', '')
                if (formId) {
                    jQuery(document).trigger('gform_post_render', [parseInt(formId), 1])
                }
            })
        } catch (e) {}
    }

    // imagesLoaded callback - trigger resize after images load
    if (typeof window.imagesLoaded !== 'undefined') {
        try {
            window.imagesLoaded(document.body, () => {
                window.dispatchEvent(new Event('resize'))
            })
        } catch (e) {}
    }

    // Elementor frontend reinit
    if (typeof window.elementorFrontend !== 'undefined') {
        try {
            window.elementorFrontend.init()
            // Reinit all widgets
            if (window.elementorFrontend.elementsHandler) {
                window.elementorFrontend.elementsHandler.runReadyTrigger(document)
            }
        } catch (e) {}
    }

    // Elementor Pro (forms, popups, etc.)
    if (typeof jQuery !== 'undefined' && jQuery.fn.elementorProFrontend) {
        try {
            jQuery(window).trigger('elementor/frontend/init')
        } catch (e) {}
    }

    // WooCommerce
    if (typeof jQuery !== 'undefined') {
        try {
            // Variation forms
            jQuery('.variations_form').each(function() {
                jQuery(this).wc_variation_form()
            })
            // Quantity buttons
            jQuery(document.body).trigger('init_checkout')
            jQuery(document.body).trigger('wc_fragments_refreshed')
        } catch (e) {}
    }

    // Swiper slider
    if (typeof window.Swiper !== 'undefined') {
        try {
            document.querySelectorAll('.swiper:not(.swiper-initialized)').forEach(el => {
                new window.Swiper(el)
            })
        } catch (e) {}
    }

    // Slick slider
    if (typeof jQuery !== 'undefined' && jQuery.fn.slick) {
        try {
            jQuery('.slick-initialized').slick('setPosition')
        } catch (e) {}
    }

    // Owl Carousel
    if (typeof jQuery !== 'undefined' && jQuery.fn.owlCarousel) {
        try {
            jQuery('.owl-carousel').trigger('refresh.owl.carousel')
        } catch (e) {}
    }

    // AOS (Animate On Scroll)
    if (typeof window.AOS !== 'undefined') {
        try {
            window.AOS.refresh()
        } catch (e) {}
    }

    // GSAP ScrollTrigger
    if (typeof window.ScrollTrigger !== 'undefined') {
        try {
            window.ScrollTrigger.refresh()
        } catch (e) {}
    }
}

/**
 * Trigger page load events to reinitialize dynamic content
 * This helps sliders, lazy loaders, and other JS libraries reinitialize after AJAX navigation
 */
const triggerPageLoadEvents = (container) => {

    // 1. AjaxPress custom event (always fires)
    emitEvent('ajaxpress-content-updated', {
        container: container,
        url: window.ajaxpress_state.url
    })

    // 2. DOMContentLoaded on document (most plugins listen here)
    // Note: Some browsers may not allow re-dispatching DOMContentLoaded,
    // so we also fire a custom event that mimics it
    try {
        document.dispatchEvent(new Event('DOMContentLoaded', { bubbles: true, cancelable: true }))
    } catch (e) {
        // Fallback: fire custom event
        document.dispatchEvent(new CustomEvent('ajaxpress:DOMContentLoaded', { bubbles: true }))
    }

    // 3. Window load event (for scripts waiting for full page load)
    window.dispatchEvent(new Event('load'))

    // 4. Window resize (triggers recalculations in sliders/carousels)
    // Delayed slightly to let DOM settle
    setTimeout(() => {
        window.dispatchEvent(new Event('resize'))
    }, 50)

    // 5. jQuery ready handlers (WordPress heavily uses these)
    if (typeof jQuery !== 'undefined') {

        // Method 1: Trigger jQuery's internal ready handlers
        // jQuery stores ready handlers and they only fire once normally
        // We can force them to run again by triggering on document
        try {
            // This triggers any handlers bound via $(document).on('ready', ...)
            jQuery(document).trigger('ready')
        } catch (e) {
        }

        // Method 2: For handlers using $(function(){}) or $(document).ready()
        // These are stored in jQuery's internal queue and can't be re-triggered easily
        // But we can fire events that many plugins also listen to
        jQuery(document).trigger('ajaxpress:ready')
        jQuery(window).trigger('load')
        jQuery(window).trigger('resize')

        // Method 3: Trigger common WordPress/plugin-specific events
        jQuery(document).trigger('ready.ajaxpress')
        jQuery(document.body).trigger('post-load') // Used by Jetpack Infinite Scroll
        jQuery(document.body).trigger('init') // Generic init event
    } else {
    }

    // 6. Custom AjaxPress ready event
    const readyEvent = new CustomEvent('ajaxpress:ready', {
        bubbles: true,
        detail: { container: container, url: window.ajaxpress_state.url }
    })
    document.dispatchEvent(readyEvent)

    // 6b. Legacy ajaxpress:load event (for theme compatibility)
    document.dispatchEvent(new CustomEvent('ajaxpress:load', {
        bubbles: true,
        detail: { container: container, url: window.ajaxpress_state.url }
    }))

    // 7. Reinitialize common lazy load libraries
    reinitLazyLoaders()

    // 8. Reinitialize theme and plugin scripts (sliders, popups, etc.)
    reinitThemeScripts()

    // 9. Force reflow to trigger CSS animations and transitions
    void container.offsetHeight

}

// Check if content animation is enabled
export const isContentAnimationEnabled = () => {
    const settings = ajaxpress_vars.settings || {}
    const license = ajaxpress_vars?.license || {}
    const isLicenseValid = !!(license.key && license.status === 'active')
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches || settings.reduce_motion

    return settings.content_animation && isLicenseValid && !prefersReducedMotion
}

// Get content animation duration
const getAnimationDuration = () => {
    const settings = ajaxpress_vars.settings || {}
    const duration = settings.content_animation_duration
    if (duration === 'custom') {
        return parseFloat(settings.content_animation_duration_custom) || 0.3
    }
    return parseFloat(duration) || 0.3
}

// All animation class names
const animationClasses = [
    'ajaxpress-animate-fade-out', 'ajaxpress-animate-fade-in',
    'ajaxpress-animate-slide-out', 'ajaxpress-animate-slide-in',
    'ajaxpress-animate-flip-out', 'ajaxpress-animate-flip-in',
    'ajaxpress-animate-scale-out', 'ajaxpress-animate-scale-in'
]

// Animate out content and return promise that resolves when complete
export const fadeOutContent = () => {
    if (!isContentAnimationEnabled()) {
        return Promise.resolve()
    }

    const settings = ajaxpress_vars.settings || {}
    const container = document.querySelector(settings.target || 'body') || document.body
    const animationName = settings.content_animation_name || 'fade'
    const duration = getAnimationDuration()

    // Remove any existing animation classes
    container.classList.remove(...animationClasses)

    // Force reflow
    void container.offsetWidth

    // Set animation duration
    container.style.setProperty('--ajaxpress-animation-duration', `${duration / 2}s`)

    // Add animation-specific out class
    container.classList.add(`ajaxpress-animate-${animationName}-out`)

    // Return promise that resolves after animation completes
    return new Promise(resolve => {
        setTimeout(() => {
            resolve()
        }, (duration / 2) * 1000)
    })
}

// Animate in content after replacement
export const fadeInContent = () => {
    const settings = ajaxpress_vars.settings || {}
    const container = document.querySelector(settings.target || 'body') || document.body

    if (!isContentAnimationEnabled()) {
        // No animation - just remove classes and ensure visible
        container.classList.remove(...animationClasses)
        container.style.opacity = ''
        container.style.transform = ''
        return Promise.resolve()
    }

    const animationName = settings.content_animation_name || 'fade'
    const duration = getAnimationDuration()
    const animationDuration = (duration / 2) * 1000

    // Remove out class
    container.classList.remove(`ajaxpress-animate-${animationName}-out`)

    // Force reflow
    void container.offsetWidth

    // Set animation duration and add in class
    container.style.setProperty('--ajaxpress-animation-duration', `${duration / 2}s`)
    container.classList.add(`ajaxpress-animate-${animationName}-in`)

    // After animation completes, clean up
    return new Promise(resolve => {
        setTimeout(() => {
            container.classList.remove(`ajaxpress-animate-${animationName}-in`)
            resolve()
        }, animationDuration)
    })
}

export const loadContent = async (url, source = 'click') => {
    return new Promise ( async (resolve, reject) => {
        // Ensure we have a valid URL
        if (!url || typeof url !== 'string') {
            reject(new Error('Invalid URL'));
            return;
        }

        window.ajaxpress_state.url = url
        window.ajaxpress_state.loading = true

        try {
            // 1. Fetch content first (progress bar shows during this)
            const html = await fetchContent(url, source)

            // 2. Fade out current content
            await fadeOutContent()

            // 3. Scroll to top while in dark (instant, user won't see)
            if (ajaxpress_vars.settings?.scroll_to_top && source !== 'comment') {
                window.scrollTo(0, 0)
            }

            // 4. Replace content in dark
            await updateContent(html)

            // 5. Small pause to ensure DOM is ready
            await new Promise(r => setTimeout(r, 50))

            // 6. Fire updated event (hides progress bar, loader, etc.)
            emitEvent('ajaxpress-updated', {
                html: html,
                source: window.ajaxpress_state.updateSource || source
            })

            // 7. Fade in new content
            await fadeInContent()

            window.ajaxpress_state.loading = false
            resolve(true)
        } catch (err) {
            window.ajaxpress_state.loading = false
            reject(err)
        }
    })
}